import React from "react";
import { Button } from "../ui/components/Button";

interface PrefillField {
  label: string;
  currentValue: string;
  newValue: string;
}

interface GSTINPrefillDialogProps {
  isOpen: boolean;
  entityType: "vendor" | "customer";
  fields: PrefillField[];
  onApplyAll: () => void;
  onClose: () => void;
}

const GSTINPrefillDialog: React.FC<GSTINPrefillDialogProps> = ({
  isOpen,
  entityType,
  fields,
  onApplyAll,
  onClose,
}) => {
  if (!isOpen) return null;

  const changedFields = fields.filter((f) => f.newValue && f.newValue !== f.currentValue);

  if (changedFields.length === 0) return null;

  // Check if the name field has a mismatch (current name exists but differs from GSTIN name)
  const nameField = changedFields.find(
    (f) => f.label.includes("Name") && f.currentValue && f.currentValue.trim() !== ""
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-default-font">
            Auto-fill from GSTIN Verification
          </h3>
          <p className="text-sm text-neutral-500 mt-1">
            The following {entityType} details can be updated from the GSTIN
            verification result.
          </p>
        </div>

        <div className="px-6 py-4 max-h-80 overflow-y-auto">
          {nameField && (
            <div className="flex items-start gap-2 text-sm bg-yellow-50 border border-yellow-200 px-3 py-2 rounded mb-4">
              <svg
                className="h-4 w-4 mt-0.5 flex-shrink-0 text-yellow-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-yellow-700">
                GSTIN is registered to <strong>{nameField.newValue}</strong>.
                This doesn't match the current {entityType} name "<strong>{nameField.currentValue}</strong>".
                Clicking "Apply All" will update the name.
              </span>
            </div>
          )}

          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col className="w-[120px]" />
              <col />
              <col />
            </colgroup>
            <thead>
              <tr className="text-left text-neutral-500">
                <th className="pb-2 pr-3 font-medium">Field</th>
                <th className="pb-2 pr-3 font-medium">Current</th>
                <th className="pb-2 font-medium">New</th>
              </tr>
            </thead>
            <tbody>
              {changedFields.map((field, i) => (
                <tr key={i} className="border-t border-neutral-100">
                  <td className="py-2 pr-3 font-medium text-default-font align-top whitespace-nowrap">
                    {field.label}
                  </td>
                  <td className="py-2 pr-3 text-neutral-500 align-top break-words">
                    {field.currentValue || "-"}
                  </td>
                  <td className="py-2 text-green-600 align-top break-words">
                    {field.newValue}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <Button variant="neutral-secondary" size="small" onClick={onClose}>
            Skip
          </Button>
          <Button variant="brand-primary" size="small" onClick={onApplyAll}>
            Apply All
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GSTINPrefillDialog;
